import { GetAppointment } from "@/services/fhir";
import { GetPatient } from "@/services/fhir/patient/GetPatient";
import { currentFeatures } from "@/configs/Settings";
import type { TeleconsultoriaReceivedInvite } from "../types";
import {
  isTeleconsultoriaParticipant,
  teleconsultoriaGuestRoomUrl,
} from "../utils/actors";
import {
  organizationIdFromReference,
  resolveOrganizationDisplays,
} from "./resolveOrganizationDisplays";

function appointmentsFromBundle(bundle?: fhir5.Bundle): fhir5.Appointment[] {
  return (bundle?.entry || [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Appointment =>
        resource?.resourceType === "Appointment",
    );
}

/** Locations vindas pelo _include, indexadas por id. */
function locationsFromBundle(bundle?: fhir5.Bundle): Map<string, fhir5.Location> {
  const locations = new Map<string, fhir5.Location>();
  (bundle?.entry || []).forEach((entry) => {
    const resource = entry.resource;
    if (resource?.resourceType === "Location" && resource.id) {
      locations.set(resource.id, resource as fhir5.Location);
    }
  });
  return locations;
}

/** Host = 1º Practitioner sem marcação CON (o teleconsultor é marcado). */
function hostDisplay(appointment: fhir5.Appointment): string | undefined {
  const host = appointment.participant?.find(
    (item) =>
      item.actor?.reference?.includes("Practitioner/") &&
      !isTeleconsultoriaParticipant(item),
  );
  return host?.actor?.display?.trim() || undefined;
}

/** Nome do local e id da organização dona dele (quando a Location veio no bundle). */
function locationDetails(
  appointment: fhir5.Appointment,
  locations: Map<string, fhir5.Location>,
) {
  const actor = appointment.participant?.find((item) =>
    item.actor?.reference?.includes("Location/"),
  )?.actor;
  const resource = locations.get(actor?.reference?.split("/")[1] || "");
  return {
    locationName: resource?.name || actor?.display || undefined,
    organizationId: organizationIdFromReference(
      resource?.managingOrganization?.reference,
    ),
  };
}

function patientActor(appointment: fhir5.Appointment) {
  return appointment.participant?.find((item) =>
    item?.actor?.reference?.includes("Patient"),
  )?.actor;
}

/**
 * Identifier de /summary.
 * No app padrão, Patient/{oid}-{cpf} vira os caracteres depois do primeiro hífen.
 */
function summaryPatientIdFromReference(reference?: string): string {
  const resourceId = String(reference || "").split("/").pop()?.trim() || "";
  if (!resourceId) return "";
  if (!currentFeatures.defaultApp) return resourceId;
  const rawAfterOid = resourceId.includes("-")
    ? resourceId.split("-").slice(1).join("-")
    : resourceId;
  return rawAfterOid.replace(/[^a-zA-Z0-9]/g, "");
}

function nameFromPatient(patient?: fhir5.Patient): string {
  const text =
    patient?.name?.find((item) => item.use === "usual" || item.use === "official")
      ?.text || patient?.name?.[0]?.text;
  return text?.trim() || "";
}

async function resolvePatientName(
  display: string | undefined,
  patientId: string,
): Promise<string> {
  const shown = display?.trim();
  if (shown) return shown;
  if (!patientId || !currentFeatures.defaultApp) return "Paciente";
  try {
    const response = await GetPatient({ identifier: patientId });
    return nameFromPatient(response?.data as fhir5.Patient | undefined) || "Paciente";
  } catch (error) {
    console.error("listReceivedTeleconsultoriaInvites patient name", error);
    return "Paciente";
  }
}

/**
 * Atendimentos (evolução ou não) em que o profissional logado é convidado
 * e a sala já existe. `teleconsultoriaGuestRoomUrl` decide por tipo.
 */
export async function listReceivedTeleconsultoriaInvites(
  practitionerRef: string,
): Promise<TeleconsultoriaReceivedInvite[]> {
  const bundle = await GetAppointment({
    professionalActor: practitionerRef,
    status: ["booked", "arrived"],
    sort: "-date",
    count: 40,
    includeLocation: true,
  });
  const locations = locationsFromBundle(bundle);

  const rows = appointmentsFromBundle(bundle)
    .map((appointment) => {
      const roomUrl = teleconsultoriaGuestRoomUrl(appointment, practitionerRef);
      if (!roomUrl || !appointment.id) return undefined;
      const patient = patientActor(appointment);
      return {
        appointment,
        appointmentId: appointment.id,
        display: patient?.display,
        patientId: summaryPatientIdFromReference(patient?.reference),
        start: appointment.start,
        roomUrl,
        practitionerName: hostDisplay(appointment),
        ...locationDetails(appointment, locations),
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  // Uma resolução para todos os convites; nunca rejeita (fallback de CNPJ).
  const organizationDisplays = await resolveOrganizationDisplays(
    rows.map((row) => row.organizationId),
  );

  return Promise.all(
    rows.map(async (row) => ({
      appointmentId: row.appointmentId,
      patientName: await resolvePatientName(row.display, row.patientId),
      patientId: row.patientId,
      start: row.start,
      roomUrl: row.roomUrl,
      appointment: row.appointment,
      practitionerName: row.practitionerName,
      locationName: row.locationName,
      organizationName: organizationDisplays.get(row.organizationId),
    })),
  );
}
